import { Injectable } from '@angular/core';
import { getBrowserLang, Translation, TranslocoService } from '@jsverse/transloco';
import Globalize from 'globalize';
import supplementalCldrData from 'devextreme-cldr-data/supplemental.json';
import en from 'devextreme/localization/messages/en.json';
import enCldrData from 'devextreme-cldr-data/en.json';
import de from 'devextreme/localization/messages/de.json';
import deCldrData from 'devextreme-cldr-data/de.json';

import 'devextreme/localization/globalize/number';
import 'devextreme/localization/globalize/date';
import 'devextreme/localization/globalize/currency';
import 'devextreme/localization/globalize/message';
import notify from 'devextreme/ui/notify';

import { Observable } from 'rxjs';
import { switchMap, tap } from 'rxjs/operators';
import { Countries } from './countries';
import { Country } from '../models/country';
import { LanguageCode } from '../models/language-code';
import { LocalizationSettingService } from './localization-setting.service';

@Injectable({
  providedIn: 'root',
})
export class GlobalizeLanguageService {
  private translatedLangs: string[];
  private countries: Countries;

  constructor(private localizationSettingsService: LocalizationSettingService, private translocoService: TranslocoService) {
    this.translatedLangs = [];
    this.countries = new Countries('./assets/img/language-transloco-icons/');
  }

  loadLanguageForAppInitialization(): Observable<Translation> {
    return this.localizationSettingsService.getSettings().pipe(
      switchMap((settings) => {
        // if only one language is set in config, take exactly this one
        if (!settings.multiLang && settings.productLanguageCodes && settings.productLanguageCodes.length === 1) {
          const languageCode = settings.productLanguageCodes[0];
          return this.translocoService.load(settings.productLanguageCodes[0]).pipe(
            tap(() => {
              this.initGlobalize(languageCode);
              this.setLanguage(languageCode);
              this.translocoService.setActiveLang(languageCode);
            })
          );
        }

        const localstorageLanguage = localStorage.getItem('language') ? localStorage.getItem('language') : '';
        const browserLanguage = getBrowserLang();
        const availableLanguages: LanguageCode[] = this.countries.getLanguageCodes();

        // if more than one language is set and no language is found in locals storage, take the first in config
        if (!localstorageLanguage && settings.multiLang && settings.productLanguageCodes && settings.productLanguageCodes.length > 1) {
          const languageCode = settings.productLanguageCodes[0];
          return this.translocoService.load(settings.productLanguageCodes[0]).pipe(
            tap(() => {
              this.initGlobalize(languageCode);
              this.setLanguage(languageCode);
              this.translocoService.setActiveLang(languageCode);
            })
          );
        }

        // if language is in local storage and a valid language, take it
        if (localstorageLanguage && this.isValidLanguage(localstorageLanguage, availableLanguages)) {
          return this.translocoService.load(localstorageLanguage).pipe(
            tap(() => {
              this.initGlobalize(localstorageLanguage);
              this.setLanguage(localstorageLanguage);
              this.translocoService.setActiveLang(localstorageLanguage);
            })
          );
        }

        // if language is in browser set and a valid language, take it
        if (browserLanguage && this.isValidLanguage(browserLanguage, availableLanguages)) {
          return this.translocoService.load(browserLanguage).pipe(
            tap(() => {
              this.initGlobalize(browserLanguage);
              this.setLanguage(browserLanguage);
              this.translocoService.setActiveLang(browserLanguage);
            })
          );
        }

        // Fallback language if no language is found in localstorage or browser
        return this.translocoService.load(LanguageCode.DE).pipe(
          tap(() => {
            this.initGlobalize(LanguageCode.DE);
            this.setLanguage(LanguageCode.DE);
            this.translocoService.setActiveLang(LanguageCode.DE);
          })
        );
      })
    );
  }

  // All languages in enum LanguageCode are valid languages
  private isValidLanguage(language: string, availableLanguages: LanguageCode[]): language is LanguageCode {
    return availableLanguages.includes(language as LanguageCode);
  }

  public setLanguage(lang: string) {
    if (this.translatedLangs.indexOf(lang) > -1) {
      Globalize.locale(lang);
    }
  }

  public getCountries(): Country[] {
    return this.countries.getCountries();
  }

  private initGlobalize(languageCode: string): void {
    const translatedLanguages: LanguageCode[] = this.countries.getLanguageCodes();

    const messages = {
      en, // devextreme/localization/messages/en.json
      de, // devextreme/localization/messages/de.json
    };

    Globalize.load(enCldrData);
    Globalize.load(deCldrData);
    Globalize.load(supplementalCldrData);

    for (const translatedLanguage of translatedLanguages) {
      try {
        this.translatedLangs.push(translatedLanguage);
        Globalize.loadMessages(messages[translatedLanguage]);
      } catch (error) {
        notify({
          message: 'Please also add Globalize Messages in language.service for Language: ' + translatedLanguage,
          type: 'Error',
          displayTime: 10000,
        });
      }
    }
    Globalize.locale(languageCode);
  }
}
